import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type { ListFilterPayload } from '@/types/filters';
import type { ModuleDto, ModuleMappingDto } from '@/types/module';

// ============================================
// Query Params
// ============================================

interface GetModulesParams {
  search?: string;
  page?: number;
  size?: number;
  sort?: string;
}

interface GetModuleMappingsParams {
  page?: number;
  size?: number;
  sort?: string;
}

// ============================================
// Modules (read-only, pre-seeded)
// ============================================

export const getModules = async (
  params: GetModulesParams = {},
  body?: ListFilterPayload,
): Promise<PaginatedResponse<ModuleDto>> => {
  const response = await api.post<PaginatedResponse<ModuleDto>>(
    '/platform-service/v1/modules/list',
    body ?? {},
    { params },
  );
  return response.data;
};

// ============================================
// Module Mappings (per entity)
// ============================================

export const getModuleMappings = async (
  organizationId: string,
  params: GetModuleMappingsParams = {},
): Promise<PaginatedResponse<ModuleMappingDto>> => {
  const response = await api.get<PaginatedResponse<ModuleMappingDto>>(
    `/platform-service/v1/module-mappings/${organizationId}`,
    { params },
  );
  return response.data;
};

/**
 * Reconcile an organization's module mappings to exactly the given set in a
 * single call. The backend diffs against the current rows: any module id in
 * `desiredModuleIds` that isn't already mapped is added, any currently mapped
 * module id absent from the list is removed. Replaces the previous
 * "bulk-POST adds + N per-row DELETEs" client-side diff dance with one request
 * and emits one `CLIENT_MODULES_UPDATED` audit row server-side. Platform-typed
 * modules (Envelopes, Document Requests, Audit, Organization) that the legacy
 * per-row delete used to reject are removed here too — the sync endpoint
 * treats the supplied list as authoritative.
 */
export const syncModuleMappings = async (
  organizationId: string,
  desiredModuleIds: string[],
): Promise<ModuleMappingDto[]> => {
  const response = await api.put<ModuleMappingDto[]>(
    `/platform-service/v1/module-mappings/${organizationId}/sync`,
    desiredModuleIds,
    { successToast: true },
  );
  return response.data;
};
